// Admin sign-in delegated to l2u-identity (the central identity provider).
//
// This replaces the portal's own signInWithSSO call. The difference is which
// party holds the SAML connection: previously Okta posted its assertion to THIS
// project's ACS, so every portal needed its own SAML app. Now l2u-identity is
// the SAML service provider, this project federates to it over OIDC, and Okta
// only ever talks to l2u-identity. The portal still mints its own session, so
// RLS and auth.uid() are unchanged.
//
// Authorisation is emphatically NOT delegated: l2u-identity answers only "is
// this really x@company.com". Whether that person may use this portal stays with
// adminAuth/attemptSsoUserBackfill, and /sso/callback still confirms it via
// /api/auth/admin/me before letting anyone in.

/**
 * The custom OIDC provider registered in this project's Supabase dashboard
 * (Authentication → Add OIDC provider, name `l2u-identity`). The `custom:`
 * prefix is Supabase's convention for a dashboard-registered OIDC provider —
 * sign-in fails with "provider is not enabled" if the name here and the name
 * registered there disagree.
 */
export const CENTRAL_IDENTITY_PROVIDER = 'custom:l2u-identity';

// Declared structurally rather than as SupabaseClient['auth'] so tests can pass
// a plain object instead of standing up a real client.
interface OAuthCapableAuth {
  signInWithOAuth(params: {
    provider: string;
    options?: { redirectTo?: string; skipBrowserRedirect?: boolean };
  }): Promise<{ data: { url?: string | null } | null; error: { message: string } | null }>;
}

/**
 * Starts central sign-in and hands the browser to the returned URL.
 *
 * `skipBrowserRedirect: true` is deliberate. Left off, supabase-js navigates on
 * its own, which looks harmless but bypasses the caller's error handling: a
 * failure part-way would leave the button spinning with no notice and no
 * password fallback offered. Taking the URL back and navigating explicitly keeps
 * every outcome observable — and makes the "no URL" case, which otherwise
 * resolves silently and hangs the caller forever, a returned error instead.
 *
 * Genuinely never throws, so callers have exactly one failure path. Three
 * distinct things in here can raise, and all are inside the boundary:
 *
 *   - `getAuth()` — the client is taken as a getter, not a value, because
 *     getAdminSupabase() throws outright when VITE_SUPABASE_URL or
 *     VITE_SUPABASE_PUBLISHABLE_KEY is missing. Resolved eagerly at the call
 *     site that throw would reject the click handler, and since the button
 *     invokes `void onSsoClick()` it becomes an unhandled rejection: spinner
 *     stuck, no notice, no password fallback.
 *   - `signInWithOAuth` — an unregistered provider rejects synchronously rather
 *     than returning an error object.
 *   - `navigate` — caller-supplied and arbitrary; assigning
 *     window.location.href can raise.
 */
export function createCentralSignIn(
  getAuth: () => OAuthCapableAuth,
  navigate: (url: string) => void,
) {
  return async (redirectTo: string): Promise<{ error: { message: string } | null }> => {
    try {
      const { data, error } = await getAuth().signInWithOAuth({
        provider: CENTRAL_IDENTITY_PROVIDER,
        options: { redirectTo, skipBrowserRedirect: true },
      });

      if (error) return { error };
      if (!data?.url) {
        return { error: { message: 'Central sign-in did not return a redirect URL.' } };
      }

      navigate(data.url);
      return { error: null };
    } catch (err) {
      return {
        error: { message: err instanceof Error ? err.message : 'Central sign-in failed.' },
      };
    }
  };
}
